// @flow
import {
  getAddressesApi,
} from '../../preferences/saved-addresses/api';
import update from 'immutability-helper';
import {Observable, of} from 'rxjs';
import {ofType, StateObservable} from 'redux-observable';
import {push} from 'react-router-redux';
import {setNewAlert} from '../../alert/duck';
import {catchError, map, mergeMap, switchMap, filter, debounceTime} from 'rxjs/operators';
import {fillOrder} from '../CheckoutSuccessStep/duck';
import analytics from '../../../analytics';
import {
  endpoints, fetchPaymentMethodsApi, fetchOrderApi,
  postCreateCartOrderAndTokenApi, postCreateBagOrderAndTokenApi, putOrderPaymentMethodApi,
  postPaymentExecuteApi, postCreateItemsOrderAndTokenApi, applyCouponApi, removeCouponApi,
} from './api';
import {deleteShoppingCartItems} from '../../shopping-cart/duck';
import routes from '../../../routes';

import type {Address, Bag, Country, Order, ShoppingCartItem} from '../../../entities';
import type {ForecastType} from '../QuoteStep/api';
import type {
  CardOrderAndTokenReq, UpdateOrderPaymentMethodReq, PaymentExecuteReq, ItemsOrderAndTokenReq,
} from './api';
import type {HTTPError} from '../../../error';
import type {Forecast} from '../QuoteStep/api.js';
import type {RootState} from '../../index';

export type PaymentExecuteUIError = {
  code: string;
  name: string;
};

export type CheckoutStepAction = {
  +type: string;
  +addresses?: Address[];
  +error?: HTTPError;
  +requestCountry?: Country;
  +paymentMethods?: { [key: string]: string };
  +importMethod?: string;
  +cardOrderAndTokenReq?: CardOrderAndTokenReq;
  +order?: Order;
  +updateOrderPaymentMethodReq?: UpdateOrderPaymentMethodReq;
  +orderId?: string;
  +paymentId?: string;
  +paymentExecuteReq?: PaymentExecuteReq;
  +paymentExecuteUIError?: PaymentExecuteUIError;
  +forecast?: Forecast;
  +selectedIndex?: number;
  +appliedCoupon?: string;
  // This is for new checkout method /checkout/items
  +shoppingCartItems?: ShoppingCartItem[];
  +itemsOrderAndTokenReq?: ItemsOrderAndTokenReq;
  +couponCode?: string;
};

export type CheckoutStepState = {
  +isFetchingAddresses?: boolean;
  +fetchAddressesError?: HTTPError;
  +addresses?: Address[];
  +isFetchingPaymentMethods?: boolean;
  +fetchPaymentMethodsError?: HTTPError;
  +paymentMethods?: { [key: string]: string };
  +importMethod?: string;
  +isCreatingOrderAndToken?: boolean;
  +createOrderAndTokenError?: HTTPError;
  +isUpdatingOrderPaymentMethod?: boolean;
  +updateOrderPaymentMethodError?: HTTPError;
  +isFetchingOrder?: boolean;
  +fetchOrderError?: HTTPError;
  +order?: Order;
  +isPostingPaymentExecute?: boolean;
  +paymentExecuteSuccess?: boolean;
  +paymentExecuteError?: HTTPError;
  +paymentExecuteUIError?: HTTPError;
  +forecast?: Forecast;
  +selectedIndex?: number;
  +appliedCoupon?: string;
  +paymentGetLibraryError?: HTTPError;
  // This is for new checkout method /checkout/items
  +shoppingCartItems?: ShoppingCartItem[];
  +isApplyingCoupon?: boolean;
  +applyCouponError?: HTTPError;
};

const CHECKOUT_STEP_FETCH_ORDER_REQUEST = 'CHECKOUT_STEP_FETCH_ORDER_REQUEST';
const CHECKOUT_STEP_FETCH_ORDER_SUCCESS = 'CHECKOUT_STEP_FETCH_ORDER_SUCCESS';
const CHECKOUT_STEP_FETCH_ORDER_ERROR = 'CHECKOUT_STEP_FETCH_ORDER_ERROR';

const CHECKOUT_STEP_FETCH_ADDRESSES_REQUEST = 'CHECKOUT_STEP_FETCH_ADDRESSES_REQUEST';
const CHECKOUT_STEP_FETCH_ADDRESSES_SUCCESS = 'CHECKOUT_STEP_FETCH_ADDRESSES_SUCCESS';
const CHECKOUT_STEP_FETCH_ADDRESSES_ERROR = 'CHECKOUT_STEP_FETCH_ADDRESSES_ERROR';

const CHECKOUT_STEP_FETCH_CHECKOUT_DATA_SUCCESS = 'CHECKOUT_STEP_FETCH_CHECKOUT_DATA_SUCCESS';
const CHECKOUT_STEP_FETCH_APPLIED_COUPON_SUCCESS = 'CHECKOUT_STEP_FETCH_APPLIED_COUPON_SUCCESS';
const CHECKOUT_STEP_REMOVE_APPLIED_COUPON = 'CHECKOUT_STEP_REMOVE_APPLIED_COUPON';

const CHECKOUT_STEP_FETCH_PAYMENT_METHOD_REQUEST = 'CHECKOUT_STEP_FETCH_PAYMENT_METHOD_REQUEST';
const CHECKOUT_STEP_FETCH_PAYMENT_METHOD_SUCCESS = 'CHECKOUT_STEP_FETCH_PAYMENT_METHOD_SUCCESS';
const CHECKOUT_STEP_FETCH_PAYMENT_METHOD_ERROR = 'CHECKOUT_STEP_FETCH_PAYMENT_METHOD_ERROR';

const CHECKOUT_STEP_APPLY_COUPON_REQUEST = 'CHECKOUT_STEP_APPLY_COUPON_REQUEST';
const CHECKOUT_STEP_APPLY_COUPON_SUCCESS = 'CHECKOUT_STEP_APPLY_COUPON_SUCCESS';
const CHECKOUT_STEP_APPLY_COUPON_ERROR = 'CHECKOUT_STEP_APPLY_COUPON_ERROR';

const CHECKOUT_STEP_REMOVE_COUPON_REQUEST = 'CHECKOUT_STEP_REMOVE_COUPON_REQUEST';
const CHECKOUT_STEP_REMOVE_COUPON_SUCCESS = 'CHECKOUT_STEP_REMOVE_COUPON_SUCCESS';
const CHECKOUT_STEP_REMOVE_COUPON_ERROR = 'CHECKOUT_STEP_REMOVE_COUPON_ERROR';

const CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_REQUEST = 'CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_REQUEST';
const CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_SUCCESS = 'CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_SUCCESS';
const CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_ERROR = 'CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_ERROR';

const CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_REQUEST = 'CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_REQUEST';
const CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_SUCCESS = 'CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_SUCCESS';
const CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_ERROR = 'CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_ERROR';

const CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_REQUEST = 'CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_REQUEST';
const CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_SUCCESS = 'CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_SUCCESS';
const CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_ERROR = 'CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_ERROR';

const CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_REQUEST = 'CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_REQUEST';
const CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_SUCCESS = 'CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_SUCCESS';
const CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_ERROR = 'CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_ERROR';

const CHECKOUT_STEP_LOADING_EXECUTE = 'CHECKOUT_STEP_LOADING_EXECUTE';
const CHECKOUT_STEP_POST_PAYMENT_EXECUTE_REQUEST = 'CHECKOUT_STEP_POST_PAYMENT_EXECUTE_REQUEST';
const CHECKOUT_STEP_POST_PAYMENT_EXECUTE_SUCCESS = 'CHECKOUT_STEP_POST_PAYMENT_EXECUTE_SUCCESS';
const CHECKOUT_STEP_POST_PAYMENT_EXECUTE_ERROR = 'CHECKOUT_STEP_POST_PAYMENT_EXECUTE_ERROR';

const CHECKOUT_STEP_ASYNC_PAYMENT_REDIRECT_TO_ORDER_REQUEST = 'CHECKOUT_STEP_ASYNC_PAYMENT_REDIRECT_TO_ORDER_REQUEST';

const CHECKOUT_STEP_POST_PAYMENT_EXECUTE_UI_ERROR = 'CHECKOUT_STEP_POST_PAYMENT_EXECUTE_UI_ERROR';

const CHECKOUT_STEP_NOT_FOUND_LIBRARY_ERROR = 'CHECKOUT_STEP_NOT_FOUND_LIBRARY_ERROR';

const CHECKOUT_STEP_RESET_STATE = 'CHECKOUT_STEP_RESET_STATE';

const CHECKOUT_STEP_NOOP = 'CHECKOUT_STEP_NOOP';

// Action creators

export function fetchOrder(orderId: string): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_ORDER_REQUEST,
    orderId,
  };
}

export function fetchOrderSuccess(order: Order): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_ORDER_SUCCESS,
    order,
  };
}

export function fetchOrderErrorFn(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_ORDER_ERROR,
    error,
  };
}


export function fetchAddresses(): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_ADDRESSES_REQUEST,
  };
}

export function fetchAddressesSuccess(addresses: Address[]): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_ADDRESSES_SUCCESS,
    addresses,
  };
}

export function fetchAddressesErrorFn(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_ADDRESSES_ERROR,
    error,
  };
}

export function asyncPaymentRedirectToOrderPage(order: Order): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_ASYNC_PAYMENT_REDIRECT_TO_ORDER_REQUEST,
    order,
  };
}

export function fetchCheckoutDataSuccess(forecast: Forecast, selectedIndex: number,
                                         importMethod: ForecastType,
                                         shoppingCartItems?: ShoppingCartItem[]): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_CHECKOUT_DATA_SUCCESS,
    forecast,
    selectedIndex,
    importMethod,
    shoppingCartItems,
  };
}

export function fetchAppliedCouponSuccess(appliedCoupon: string): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_APPLIED_COUPON_SUCCESS,
    appliedCoupon,
  };
}

export function removeAppliedCoupon(): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_REMOVE_APPLIED_COUPON,
  };
}

export function fetchPaymentMethods(requestCountry: Country): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_PAYMENT_METHOD_REQUEST,
    requestCountry,
  };
}

export function fetchPaymentMethodsSuccess(paymentMethods: { [key: string]: string }): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_PAYMENT_METHOD_SUCCESS,
    paymentMethods,
  };
}

export function fetchPaymentMethodsErrorFn(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_FETCH_PAYMENT_METHOD_ERROR,
    error,
  };
}

export function applyCoupon(couponCode: string, orderId: string): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_APPLY_COUPON_REQUEST,
    couponCode,
    orderId,
  };
}

export function applyCouponSuccess(order: Order): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_APPLY_COUPON_SUCCESS,
    order,
  };
}

export function applyCouponErrorFn(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_APPLY_COUPON_ERROR,
    error,
  };
}

export function removeCoupon(orderId: string): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_REMOVE_COUPON_REQUEST,
    orderId,
  };
}

export function removeCouponSuccess(order: Order): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_REMOVE_COUPON_SUCCESS,
    order,
  };
}

export function removeCouponErrorFn(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_REMOVE_COUPON_ERROR,
    error,
  };
}

export function createItemsOrderAndToken(itemsOrderAndTokenReq: ItemsOrderAndTokenReq,
                                         shoppingCartItems?: ShoppingCartItem[]): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_REQUEST,
    itemsOrderAndTokenReq,
    shoppingCartItems,
  };
}

export function createItemsOrderAndTokenSuccess(order: Order): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_SUCCESS,
    order,
  };
}

export function createItemsOrderAndTokenErrorFn(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_ERROR,
    error,
  };
}

export function createCartOrderAndToken(cardOrderAndTokenReq: CardOrderAndTokenReq): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_REQUEST,
    cardOrderAndTokenReq,
  }
}

export function createCartOrderAndTokenSuccess(order: Order): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_SUCCESS,
    order,
  }
}

export function createCartOrderAndTokenErrorFn(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_ERROR,
    error,
  }
}

export function createBagOrderAndToken(cardOrderAndTokenReq: CardOrderAndTokenReq, bagId?: string): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_REQUEST,
    cardOrderAndTokenReq,
    bagId,
  }
}

export function createBagOrderAndTokenSuccess(order: Order): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_SUCCESS,
    order,
  }
}

export function createBagOrderAndTokenErrorFn(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_ERROR,
    error,
  }
}

export function updateOrderPaymentMethod(updateOrderPaymentMethodReq: UpdateOrderPaymentMethodReq, orderId: string): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_REQUEST,
    updateOrderPaymentMethodReq,
    orderId,
  };
}

export function updateOrderPaymentMethodSuccess(order: Order): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_SUCCESS,
    order,
  };
}

export function updateOrderPaymentMethodErrorFn(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_ERROR,
    error,
  };
}

export function loadingExecute(): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_LOADING_EXECUTE,
  };
}

export function postPaymentExecute(paymentExecuteReq: PaymentExecuteReq, order: Order): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_POST_PAYMENT_EXECUTE_REQUEST,
    order,
    paymentExecuteReq,
  };
}

export function postPaymentExecuteSuccess(order: Order): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_POST_PAYMENT_EXECUTE_SUCCESS,
    order,
  };
}

export function postPaymentExecuteErrorFn(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_POST_PAYMENT_EXECUTE_ERROR,
    error,
  };
}

export function postPaymentExecuteUIErrorFn(paymentExecuteUIError: PaymentExecuteUIError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_POST_PAYMENT_EXECUTE_UI_ERROR,
    paymentExecuteUIError,
  };
}

export function notFoundLibraryError(error: HTTPError): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_NOT_FOUND_LIBRARY_ERROR,
    error,
  }
}

export function resetState(): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_RESET_STATE,
  };
}

export function noop(): CheckoutStepAction {
  return {
    type: CHECKOUT_STEP_NOOP,
  };
}

export const actions = {
  CHECKOUT_STEP_FETCH_ORDER_REQUEST,
  CHECKOUT_STEP_FETCH_ORDER_SUCCESS,
  CHECKOUT_STEP_FETCH_ORDER_ERROR,
  CHECKOUT_STEP_FETCH_ADDRESSES_REQUEST,
  CHECKOUT_STEP_FETCH_ADDRESSES_SUCCESS,
  CHECKOUT_STEP_FETCH_ADDRESSES_ERROR,
  CHECKOUT_STEP_FETCH_CHECKOUT_DATA_SUCCESS,
  CHECKOUT_STEP_FETCH_APPLIED_COUPON_SUCCESS,
  CHECKOUT_STEP_REMOVE_APPLIED_COUPON,
  CHECKOUT_STEP_FETCH_PAYMENT_METHOD_REQUEST,
  CHECKOUT_STEP_FETCH_PAYMENT_METHOD_SUCCESS,
  CHECKOUT_STEP_FETCH_PAYMENT_METHOD_ERROR,
  CHECKOUT_STEP_APPLY_COUPON_REQUEST,
  CHECKOUT_STEP_APPLY_COUPON_SUCCESS,
  CHECKOUT_STEP_APPLY_COUPON_ERROR,
  CHECKOUT_STEP_REMOVE_COUPON_REQUEST,
  CHECKOUT_STEP_REMOVE_COUPON_SUCCESS,
  CHECKOUT_STEP_REMOVE_COUPON_ERROR,
  CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_REQUEST,
  CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_SUCCESS,
  CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_ERROR,
  CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_REQUEST,
  CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_SUCCESS,
  CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_ERROR,
  CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_REQUEST,
  CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_SUCCESS,
  CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_ERROR,
  CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_REQUEST,
  CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_SUCCESS,
  CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_ERROR,
  CHECKOUT_STEP_LOADING_EXECUTE,
  CHECKOUT_STEP_POST_PAYMENT_EXECUTE_REQUEST,
  CHECKOUT_STEP_POST_PAYMENT_EXECUTE_SUCCESS,
  CHECKOUT_STEP_POST_PAYMENT_EXECUTE_ERROR,
  CHECKOUT_STEP_POST_PAYMENT_EXECUTE_UI_ERROR,
  CHECKOUT_STEP_ASYNC_PAYMENT_REDIRECT_TO_ORDER_REQUEST,
  CHECKOUT_STEP_NOT_FOUND_LIBRARY_ERROR,
  CHECKOUT_STEP_RESET_STATE,
  fetchOrder,
  fetchOrderSuccess,
  fetchOrderErrorFn,
  fetchAddresses,
  fetchCheckoutDataSuccess,
  fetchAppliedCouponSuccess,
  removeAppliedCoupon,
  fetchAddressesSuccess,
  fetchAddressesErrorFn,
  fetchPaymentMethods,
  fetchPaymentMethodsSuccess,
  fetchPaymentMethodsErrorFn,
  applyCoupon,
  applyCouponSuccess,
  applyCouponErrorFn,
  removeCoupon,
  removeCouponSuccess,
  removeCouponErrorFn,
  createItemsOrderAndToken,
  createItemsOrderAndTokenSuccess,
  createItemsOrderAndTokenErrorFn,
  createCartOrderAndToken,
  createCartOrderAndTokenSuccess,
  createCartOrderAndTokenErrorFn,
  createBagOrderAndToken,
  createBagOrderAndTokenSuccess,
  createBagOrderAndTokenErrorFn,
  updateOrderPaymentMethod,
  updateOrderPaymentMethodSuccess,
  updateOrderPaymentMethodErrorFn,
  loadingExecute,
  postPaymentExecute,
  postPaymentExecuteSuccess,
  postPaymentExecuteErrorFn,
  postPaymentExecuteUIErrorFn,
  asyncPaymentRedirectToOrderPage,
  notFoundLibraryError,
  resetState,
};

export const initialState: CheckoutStepState = {};

// Reducer
export default function reducer(state: CheckoutStepState = initialState,
                                action: CheckoutStepAction): CheckoutStepState {
  switch (action.type) {
    case CHECKOUT_STEP_FETCH_ORDER_REQUEST:
      return update(state, {
        isFetchingOrder: {$set: true},
      });
    case CHECKOUT_STEP_FETCH_ORDER_SUCCESS:
      return update(state, {
        $unset: ['isFetchingOrder'],
        order: {$set: action.order},
      });
    case CHECKOUT_STEP_FETCH_ORDER_ERROR:
      return update(state, {
        $unset: ['isFetchingOrder'],
        fetchOrderError: {$set: action.error},
      });
    case CHECKOUT_STEP_FETCH_ADDRESSES_REQUEST:
      return update(state, {
        isFetchingAddresses: {$set: true},
        $unset: ['addresses'],
      });
    case CHECKOUT_STEP_FETCH_ADDRESSES_SUCCESS:
      return update(state, {
        addresses: {$set: action.addresses},
        $unset: ['isFetchingAddresses'],
      });
    case CHECKOUT_STEP_FETCH_ADDRESSES_ERROR:
      return update(state, {
        fetchAddressesError: {$set: action.error},
        $unset: ['isFetchingAddresses'],
      });
    case CHECKOUT_STEP_FETCH_CHECKOUT_DATA_SUCCESS:
      return update(state, {
        forecast: {$set: action.forecast},
        selectedIndex: {$set: action.selectedIndex},
        importMethod: {$set: action.importMethod},
        shoppingCartItems: {$set: action.shoppingCartItems},
      });
    case CHECKOUT_STEP_FETCH_APPLIED_COUPON_SUCCESS:
      return update(state, {
        appliedCoupon: {$set: action.appliedCoupon},
      });
    case CHECKOUT_STEP_FETCH_PAYMENT_METHOD_REQUEST:
      return update(state, {
        $unset: ['paymentMethods', 'fetchPaymentMethodsError'],
        isFetchingPaymentMethods: {$set: true},
      });
    case CHECKOUT_STEP_FETCH_PAYMENT_METHOD_SUCCESS:
      return update(state, {
        $unset: ['isFetchingPaymentMethods'],
        paymentMethods: {$set: action.paymentMethods},
      });
    case CHECKOUT_STEP_FETCH_PAYMENT_METHOD_ERROR:
      return update(state, {
        $unset: ['paymentMethods', 'isFetchingPaymentMethods'],
        fetchPaymentMethodsError: {$set: action.error},
      });
    case CHECKOUT_STEP_APPLY_COUPON_REQUEST:
      return update(state, {
        $unset: ['applyCouponError'],
        isApplyingCoupon: {$set: true},
      });
    case CHECKOUT_STEP_APPLY_COUPON_SUCCESS:
      return update(state, {
        $unset: ['isApplyingCoupon', 'applyCouponError'],
        order: {$set: action.order},
      });
    case CHECKOUT_STEP_APPLY_COUPON_ERROR:
      return update(state, {
        $unset: ['isApplyingCoupon'],
        applyCouponError: {$set: action.error},
      });
    case CHECKOUT_STEP_REMOVE_COUPON_REQUEST:
      return update(state, {
        $unset: ['applyCouponError'],
        isApplyingCoupon: {$set: true},
      });
    case CHECKOUT_STEP_REMOVE_COUPON_SUCCESS:
      return update(state, {
        $unset: ['isApplyingCoupon', 'applyCouponError'],
        order: {$set: action.order},
      });
    case CHECKOUT_STEP_REMOVE_COUPON_ERROR:
      return update(state, {
        $unset: ['isApplyingCoupon'],
        applyCouponError: {$set: action.error},
      });
    case CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_REQUEST:
    case CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_REQUEST:
    case CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_REQUEST:
      return update(state, {
        isCreatingOrderAndToken: {$set: true},
        $unset: ['fetchPaymentMethodsError',
          'createOrderAndTokenError',
          'updateOrderPaymentMethodError',
          'paymentExecuteError'],
      });
    case CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_SUCCESS:
    case CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_SUCCESS:
    case CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_SUCCESS:
      return update(state, {
        $unset: ['isCreatingOrderAndToken'],
        order: {$set: action.order},
      });
    case CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_ERROR:
    case CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_ERROR:
    case CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_ERROR:
      return update(state, {
        $unset: ['isCreatingOrderAndToken'],
        createOrderAndTokenError: {$set: action.error},
      });
    case CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_REQUEST:
      return update(state, {
        isUpdatingOrderPaymentMethod: {$set: true},
      });
    case CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_SUCCESS:
      return update(state, {
        $unset: ['isUpdatingOrderPaymentMethod'],
        order: {$set: action.order},
      });
    case CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_ERROR:
      return update(state, {
        $unset: ['isUpdatingOrderPaymentMethod'],
        updateOrderPaymentMethodError: {$set: action.error},
      });
    case CHECKOUT_STEP_LOADING_EXECUTE:
      return update(state, {
        isPostingPaymentExecute: {$set: true},
      });
    case CHECKOUT_STEP_POST_PAYMENT_EXECUTE_REQUEST:
      return update(state, {
        $unset: ['paymentExecuteError'],
        isPostingPaymentExecute: {$set: true},
      });
    case CHECKOUT_STEP_POST_PAYMENT_EXECUTE_SUCCESS:
      return update(state, {
        $unset: ['isPostingPaymentExecute', 'paymentExecuteError'],
        paymentExecuteSuccess: {$set: true},
      });
    case CHECKOUT_STEP_POST_PAYMENT_EXECUTE_ERROR:
      return update(state, {
        $unset: ['isPostingPaymentExecute'],
        paymentExecuteError: {$set: action.error},
      });
    case CHECKOUT_STEP_POST_PAYMENT_EXECUTE_UI_ERROR:
      return update(state, {
        paymentExecuteUIError: {$set: action.paymentExecuteUIError},
      });
    case CHECKOUT_STEP_NOT_FOUND_LIBRARY_ERROR:
      return update(state, {
        paymentGetLibraryError: {$set: action.error},
      });
    case CHECKOUT_STEP_RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

// Epics
export function fetchCheckoutStepOrderEpic(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_FETCH_ORDER_REQUEST),
    mergeMap(({orderId}) => fetchOrderApi(undefined, {
        route: endpoints.FETCH_ORDER.replace(':orderId', orderId),
      }).pipe(
      map((order: Order) => {
        if (order.invoice && order.invoice.state !== 'PENDING') {
          // TODO: handle if the order is already a transaction or of it's another url
          return push(`/purchase/order/${order.id}`);
        }
        return fetchOrderSuccess(order);
      }),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchOrderErrorFn(e),
            push(`/purchase/order/${orderId}`),
            // setNewAlert('checkout', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(fetchOrderErrorFn(e),
            push(`/purchase/order/${orderId}`),
            // setNewAlert('checkout', 'error', undefined, 'httpErrors.post.400'),
          );
        } else if (e.code === 404) {
          return of(fetchOrderErrorFn(e),
            push('/new-order'))
        }
        return of(fetchOrderErrorFn(e));
      })),
    ));
}

export function fetchNewOrderCheckoutAddressesEpic(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_FETCH_ADDRESSES_REQUEST),
    mergeMap(() => getAddressesApi().pipe(
      map((addresses: Address[]) => fetchAddressesSuccess(addresses)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchAddressesErrorFn(e),
            setNewAlert('checkout', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(fetchAddressesErrorFn(e),
            setNewAlert('checkout', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(fetchAddressesErrorFn(e));
      })),
    ));
}

/*
export function fetchNewOrderCheckoutBagEpic(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_FETCH_BAG_REQUEST),
    mergeMap(({ bagId }) => fetchBagApi(undefined, {
        route: endpoints.FETCH_BAG.replace(':bagId', bagId),
      }).pipe(
      mergeMap((bag: Bag) => {
        if (bag.id !== bagId) {
          return of(fetchBagSuccess(bag), push(`/new-order/quote/bag/${bag.id}`));
        }
        return of(fetchBagSuccess(bag))
      }),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchBagError(e),
            setNewAlert('new-order, 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(fetchBagError(e),
            setNewAlert('new-order, 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(fetchBagError(e));
      }))
    ));
}
*/

export function fetchNewOrderCheckoutPaymentMethodsEpic(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_FETCH_PAYMENT_METHOD_REQUEST),
    mergeMap(({requestCountry}) => fetchPaymentMethodsApi(undefined, {
        route: endpoints.FETCH_PAYMENT_METHODS.replace(':country', requestCountry),
      }).pipe(
      map((paymentMethods: { [key: string]: string }) => fetchPaymentMethodsSuccess(paymentMethods)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchPaymentMethodsErrorFn(e),
            setNewAlert('checkout', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(fetchPaymentMethodsErrorFn(e),
            setNewAlert('checkout', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(fetchPaymentMethodsErrorFn(e));
      })),
    ));
}

export function postNewOrderCheckoutItemsOrderAndTokenEpic(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_CREATE_ITEMS_ORDER_AND_TOKEN_REQUEST),
    mergeMap(({itemsOrderAndTokenReq}) => {
      const shoppingCartItemIds = itemsOrderAndTokenReq.items.map(i => i.id);
      const req = {
        ...itemsOrderAndTokenReq,
        items: itemsOrderAndTokenReq.items.map(i => {
          const it = {...i};
          delete it.id;
          return it;
        }),
      };
      return postCreateItemsOrderAndTokenApi(req)
        .pipe(
          mergeMap((order: Order) => of(
            createItemsOrderAndTokenSuccess(order),
            deleteShoppingCartItems(shoppingCartItemIds),
          )),
          catchError((e: HTTPError) => {
            if (Math.round(e.code / 100) === 5) {
              return of(createItemsOrderAndTokenErrorFn(e),
                setNewAlert('checkout', 'error', undefined, 'httpErrors.post.500'),
              );
            } else if (e.code === 400) {
              return of(createItemsOrderAndTokenErrorFn(e),
                setNewAlert('checkout', 'error', undefined, 'httpErrors.post.400'),
              );
            } else if (e.code === 404) {
              if (e.data && e.data.name === 'CouponNotFoundOrInvalidError') {
                return of(createBagOrderAndTokenErrorFn(e),
                  setNewAlert('checkout', 'error', undefined, 'newOrder.checkout.error.couponNotValid'),
                );
              }
            }
            return of(createItemsOrderAndTokenErrorFn(e));
          })
        );
    })
  );
}

export function postNewOrderCheckoutCartOrderAndTokenEpic(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_REQUEST),
    mergeMap(({cardOrderAndTokenReq}) => postCreateCartOrderAndTokenApi(cardOrderAndTokenReq)
      .pipe(
        mergeMap((order: Order) => of(
          createCartOrderAndTokenSuccess(order),
        )),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(createCartOrderAndTokenErrorFn(e),
              setNewAlert('checkout', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(createCartOrderAndTokenErrorFn(e),
              setNewAlert('checkout', 'error', undefined, 'httpErrors.post.400'),
            );
          } else if (e.code === 404) {
            if (e.data && e.data.name === 'CouponNotFoundOrInvalidError') {
              return of(createBagOrderAndTokenErrorFn(e),
                setNewAlert('checkout', 'error', undefined, 'newOrder.checkout.error.couponNotValid'),
              );
            }
          }
          return of(createCartOrderAndTokenErrorFn(e));
        })),
    ));
}

export function postNewOrderCheckoutBagOrderAndTokenEpic(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_REQUEST),
    mergeMap(({cardOrderAndTokenReq, bagId}) => postCreateBagOrderAndTokenApi(cardOrderAndTokenReq, {
        route: endpoints.CREATE_BAG_ORDER_AND_TOKEN.replace(':bagId', bagId),
      }).pipe(
      map((order: Order) => createBagOrderAndTokenSuccess(order)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(createBagOrderAndTokenErrorFn(e),
            setNewAlert('checkout', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(createBagOrderAndTokenErrorFn(e),
            setNewAlert('checkout', 'error', undefined, 'httpErrors.post.400'),
          );
        } else if (e.code === 404) {
          if (e.data && e.data.name === 'CouponNotFoundOrInvalidError') {
            return of(createBagOrderAndTokenErrorFn(e),
              setNewAlert('checkout', 'error', undefined, 'newOrder.checkout.error.couponNotValid'),
            );
          }
        }
        return of(createBagOrderAndTokenErrorFn(e));
      })),
    ));
}

export function setSuccessfulCreateOrderTracking(action$: Observable<CheckoutStepAction>,
                                                 state$: StateObservable<RootState>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_CREATE_CART_ORDER_AND_TOKEN_SUCCESS, CHECKOUT_STEP_CREATE_BAG_ORDER_AND_TOKEN_SUCCESS),
    mergeMap(({order}) => {
      const paymentMethod = order.invoice && order.invoice.payment ? order.invoice.payment.method : '';
      const paymentTypeTracking = paymentMethod === 'BITPAY' || paymentMethod === 'SAFETYPAY' ?
        'async' : 'sync';
      // NOTE: This will be only for Facebook Pixel
      analytics.fireCustomEvent('Checkout Started', {
        category: 'Checkout',
        action: 'Started',
        label: order.id,
        user_type: 'purchaser',
        user_referrer: window.localStorage.getItem('userReferrer') || '',
        landing_url: window.localStorage.getItem('landingUrl') || '',
        order_id: order.id,
        order_formatted_id: order.id,
        payment_method: paymentMethod,
        payment_sub_method: order.invoice && order.invoice.payment ? order.invoice.payment.subtype : '',
        domain_country: state$.value.locale.domainCountry,
        type: paymentTypeTracking,
        products: analytics.mapProducts(order.items),
        currency: 'USD',
        value: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.total) : 0,
        subtotal: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.subtotal) : 0,
        commission: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.commission) : 0,
        shipping: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.shipping) : 0,
        tax: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.taxes) : 0,
        discount: order.invoice && order.invoice.detail ? Math.abs(parseFloat(order.invoice.detail.coupon)) : 0,
        coupon: order.couponCode || '',
        revenue: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.services) : 0,
        shipping_method: order.serviceTier,
        promised_delivery_date: order.beforeDate,
      }, {
        integrations: {
          'All': false,
          'Google Analytics': false,
          'Amplitude': false,
          'Facebook Pixel': true,
        },
      });
      return of(noop());
    }),
  );
}

export function putNewOrderCheckoutOrderPaymentMethodEpic(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_UPDATE_ORDER_PAYMENT_METHOD_REQUEST),
    mergeMap(({updateOrderPaymentMethodReq, orderId}) => putOrderPaymentMethodApi(updateOrderPaymentMethodReq, {
        route: endpoints.UPDATE_ORDER_PAYMENT_METHOD.replace(':orderId', orderId),
      }).pipe(
      map((order: Order) => updateOrderPaymentMethodSuccess(order)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(updateOrderPaymentMethodErrorFn(e),
            setNewAlert('checkout', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(updateOrderPaymentMethodErrorFn(e),
            setNewAlert('checkout', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(updateOrderPaymentMethodErrorFn(e));
      })),
    ));
}

export function postNewOrderCheckoutPaymentExecuteEpic(action$: Observable<CheckoutStepAction>,
                                                       state$: StateObservable<RootState>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_POST_PAYMENT_EXECUTE_REQUEST),
    // This is because Culqi is shit, they call the callback fn twice!!!!
    debounceTime(800),
    switchMap(({paymentExecuteReq, order}) => {
      const req = {
        ...paymentExecuteReq,
      };
      return postPaymentExecuteApi(req, {
        route: endpoints.POST_PAYMENT_EXECUTE.replace(':paymentId', order.invoice.payment.id),
      }).pipe(
        map((order: Order) => postPaymentExecuteSuccess(order)),
        catchError((e: HTTPError) => of(postPaymentExecuteErrorFn(e))))
    }));
}

export function newOrderCheckoutRedirectToSuccessPageEpic(action$: Observable<CheckoutStepAction>,
                                                          state$: StateObservable<RootState>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_POST_PAYMENT_EXECUTE_SUCCESS),
    mergeMap(({order}: { order: Order }) => {
      // NOTE: read it from google tag manager
      /*if (!!order.invoice && !!order.invoice.payment) {
        window.checkoutSuccessOrderTotal = order.invoice.payment.total;
      }*/
      // NOTE: This will be only for Facebook Pixel
      /*analytics.fireCustomEvent('Order Completed', {
        category: 'Order',
        action: 'Completed',
        label: order.id,
        user_type: 'purchaser',
        user_referrer: window.localStorage.getItem('userReferrer') || '',
        landing_url: window.localStorage.getItem('landingUrl') || '',
        order_id: order.id,
        order_formatted_id: order.id,
        payment_method: order.invoice && order.invoice.payment ? order.invoice.payment.method : '',
        payment_sub_method: order.invoice && order.invoice.payment ? order.invoice.payment.subtype : '',
        domain_country: state$.value.locale.domainCountry,
        type: 'sync',
        products: analytics.mapProducts(order.items),
        currency: 'USD',
        value: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.total) : 0,
        subtotal: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.subtotal) : 0,
        commission: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.commission) : 0,
        shipping: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.shipping) : 0,
        tax: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.taxes) : 0,
        discount: order.invoice && order.invoice.detail ? Math.abs(parseFloat(order.invoice.detail.coupon)) : 0,
        coupon: order.couponCode || '',
        revenue: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.services) : 0,
        shipping_method: order.serviceTier,
        promised_delivery_date: order.beforeDate,
      }, {
        integrations: {
          'All': false,
          'Google Analytics': false,
          'Amplitude': false,
          'Facebook Pixel': true,
        },
      });*/
      // redirect to checkout success if classic was selected
      if (!!order.serviceTier) {
        //  TODO: remove once the other implementation with Niubiz begins
        if (window && order.invoice.payment.method === 'NIUBIZ_DECOUPLED') {
          window.location.href =
            routes.NEW_ORDER.children.CHECKOUT_SUCCESS.routePath.replace(':orderId', order.id);
        }
        return of(
          fillOrder(order),
          push(routes.NEW_ORDER.children.CHECKOUT_SUCCESS.routePath.replace(':orderId', order.id)),
        );
      } else {
        //  TODO: remove once the other implementation with Niubiz begins
        if (window && order.invoice.payment.method === 'NIUBIZ_DECOUPLED') {
          window.location.href = routes.PURCHASE.routePath.replace(':id', order.id);
        }
        return of(push(routes.PURCHASE.routePath.replace(':id', order.id)));
      }
    }),
  );
}

export function newOrderCheckoutAsyncPaymentRedirectToOrderPageEpic(action$: Observable<CheckoutStepAction>,
                                                                    state$: StateObservable<RootState>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_ASYNC_PAYMENT_REDIRECT_TO_ORDER_REQUEST),
    mergeMap(({order}) => {
      analytics.fireCustomEvent('Checkout Step Completed', {
        category: 'Checkout Step',
        action: 'Completed',
        label: order.id,
        user_type: 'purchaser',
        user_referrer: window.localStorage.getItem('userReferrer') || '',
        landing_url: window.localStorage.getItem('landingUrl') || '',
        order_id: order.id,
        order_formatted_id: order.id,
        payment_method: order.invoice && order.invoice.payment ? order.invoice.payment.method : '',
        payment_sub_method: order.invoice && order.invoice.payment ? order.invoice.payment.subtype : '',
        domain_country: state$.value.locale.domainCountry,
        type: 'async',
        step: 1,
        shipping_method: order.serviceTier,
      });
      // NOTE: This fb trigger will only apply for async type, because in the case of the async we are gonna use
      // NOTE: Checkout Step Completed, right there we gonna fire Order Completed.
      // NOTE: This will be only for Facebook Pixel
      /*analytics.fireCustomEvent('Order Completed', {
        category: 'Order',
        action: 'Completed',
        label: order.id,
        user_type: 'purchaser',
        user_referrer: window.localStorage.getItem('userReferrer') || '',
        landing_url: window.localStorage.getItem('landingUrl') || '',
        order_id: order.id,
        order_formatted_id: order.id,
        payment_method: order.invoice && order.invoice.payment ? order.invoice.payment.method : '',
        payment_sub_method: order.invoice && order.invoice.payment ? order.invoice.payment.subtype : '',
        domain_country: state$.value.locale.domainCountry,
        type: 'async',
        products: analytics.mapProducts(order.items),
        currency: 'USD',
        value: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.total) : 0,
        subtotal: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.subtotal) : 0,
        commission: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.commission) : 0,
        shipping: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.shipping) : 0,
        tax: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.taxes) : 0,
        discount: order.invoice && order.invoice.detail ? Math.abs(parseFloat(order.invoice.detail.coupon)) : 0,
        coupon: order.couponCode || '',
        revenue: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.services) : 0,
        shipping_method: order.serviceTier,
        promised_delivery_date: order.beforeDate,
      }, {
        integrations: {
          'All': false,
          'Google Analytics': false,
          'Amplitude': false,
          'Facebook Pixel': true,
        },
      });*/
      return of(push(`/purchase/order/${order.id}`));
    }),
  );
}

export function postNewOrderCheckoutPaymentExecuteCustomHandleErrorEpic(action$: Observable<CheckoutStepAction>,
                                                                        state$: StateObservable<RootState>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_POST_PAYMENT_EXECUTE_ERROR),
    mergeMap(({error}) => {
      if (Math.round(error.code / 100) === 5) {
        // return of(setNewAlert('checkout', 'error', undefined, 'httpErrors.post.500'));
        return of(setNewAlert('checkout', 'error', undefined, 'newOrder.checkout.error.tryWithPaypal'));
      } else if (error.code === 400 && error.data && error.data.name === 'FailedCulqi3DS') {
        return of(setNewAlert('checkout', 'error', undefined, 'newOrder.checkout.error.failedCulqi3DS'));
      } else if (error.code === 400 && error.data && error.data.name !== 'PaymentCaptureFailed') {
        // return of(setNewAlert('checkout', 'error', undefined, 'httpErrors.post.400'));
        return of(setNewAlert('checkout', 'error', undefined, 'newOrder.checkout.error.tryWithPaypal'));
      } else if (error.code === 404 && error.data && error.data.name === 'CouponNotFoundOrInvalidError') {
        return of(setNewAlert('checkout', 'error', undefined, 'newOrder.checkout.error.couponNotValid'),
          fetchOrder(state$.value.newOrder.checkoutStep.order.id));
      } else {
        // return setNewAlert('new-order, 'error', undefined, 'httpErrors.post.others');
        return of(noop());
      }
    }),
  );
}

export function newOrderCheckoutNotFoundLibraryErrorEpic(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_NOT_FOUND_LIBRARY_ERROR),
    map(() => setNewAlert('checkout', 'error', undefined, 'newOrder.checkout.error.libraryNotFound')),
  );
}

export function newOrderCheckoutApplyCoupon(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_APPLY_COUPON_REQUEST),
    switchMap(({couponCode, orderId}) => {
      return applyCouponApi({couponCode}, {
        route: endpoints.APPLY_COUPON.replace(':orderId', orderId),
      })
        .pipe(
          map((order: Order) => {
            /*if (['AVAILABLE', 'AVAILABLE_FOR_COURIER'].includes(order.state)) {
              return push(`/purchase/order/${order.id}`);
            }*/
            return applyCouponSuccess(order);
          }),
          catchError((e: HTTPError) => of(applyCouponErrorFn(e)))
        )
    }),
  );
}

export function newOrderCheckoutRemoveCoupon(action$: Observable<CheckoutStepAction>): Observable<CheckoutStepAction> {
  return action$.pipe(
    ofType(CHECKOUT_STEP_REMOVE_COUPON_REQUEST),
    switchMap(({couponCode, orderId}) => {
      return removeCouponApi({couponCode}, {
        route: endpoints.REMOVE_COUPON.replace(':orderId', orderId),
      })
        .pipe(
          map((order: Order) => removeCouponSuccess(order)),
          catchError((e: HTTPError) => of(removeCouponErrorFn(e)))
        )
    }),
  );
}



// WEBPACK FOOTER //
// ./src/modules/new-order/CheckoutStep/duck.js